import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/stores/auth';
import { AppShell } from './AppShell';
import { BrandMark } from '@/components/brand/BrandMark';
import { ThemeToggle } from '@/components/theme/ThemeToggle';

/**
 * Layout for routes that are viewable WITHOUT signing in (service detail,
 * discover) so shared links actually open for a logged-out visitor — the
 * growth loop (feed Share, landing cards, referral links) depends on this.
 *
 * Signed-in users get the full authenticated chrome (AppShell) so their
 * experience on these pages is unchanged. Anonymous visitors get a light
 * guest header with clear sign-in / join affordances that preserve where they
 * were headed via `?next=`, and each page provides its own "sign in to
 * transact" CTA (browsing is open; booking/saving still require auth).
 */
function GuestShell() {
  const location = useLocation();
  const next = encodeURIComponent(location.pathname + location.search);
  return (
    <div className="flex min-h-full flex-col bg-makhe-bg text-makhe-ink">
      <header className="sticky top-0 z-40 border-b border-makhe-line bg-makhe-bg/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <BrandMark to="/" size={30} />
          <nav className="flex items-center gap-2 text-sm sm:gap-3">
            <ThemeToggle />
            <Link
              to={`/auth/sign-in?next=${next}`}
              className="px-2 py-2 text-sm text-makhe-muted transition-colors hover:text-makhe-ink"
            >
              Sign in
            </Link>
            <Link to={`/auth/sign-up?next=${next}`} className="btn-primary px-5 py-2.5">
              Join free
            </Link>
          </nav>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}

export function PublicShell() {
  const { initialised, firebaseUser } = useAuth();
  if (!initialised) {
    return (
      <div className="flex h-full items-center justify-center text-makhe-muted">Loading…</div>
    );
  }
  return firebaseUser ? <AppShell /> : <GuestShell />;
}
